import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useValueBenchmark } from '../hooks/useValueBenchmark';

interface BenchmarkBarProps {
  clientId: string;
}

export function BenchmarkBar({ clientId }: BenchmarkBarProps) {
  const { t } = useTranslation();
  const { data: benchmarkData, isLoading } = useValueBenchmark(clientId);
  const [isExpanded, setIsExpanded] = useState(false);
  const expandRef = useRef<HTMLDivElement>(null);
  const [expandHeight, setExpandHeight] = useState(0);

  useEffect(() => {
    if (expandRef.current) {
      setExpandHeight(expandRef.current.scrollHeight);
    }
  }, [benchmarkData]);

  if (isLoading) {
    return (
      <div
        className="rounded-xl px-4 py-3.5"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border-soft)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        <div className="skeleton h-3 w-64 rounded mb-2" />
        <div className="skeleton h-2 w-full rounded-full" />
      </div>
    );
  }

  const benchmark = benchmarkData?.data;
  if (!benchmark?.available || benchmark.overallPercentile == null) return null;

  const percentile = Math.min(100, Math.max(0, benchmark.overallPercentile));

  const toggle = () => setIsExpanded(!isExpanded);

  return (
    <div
      className="benchmark-bar rounded-xl overflow-hidden"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--secondary)',
        boxShadow: 'var(--card-shadow)',
        cursor: 'pointer',
      }}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={t('value.benchmarkTitle', { percentile })}
    >
      <div className="px-4 py-3.5">
        <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>
          {t('value.benchmarkTitle', { percentile })}
        </p>

        {/* Progress bar */}
        <div className="relative mt-2 mb-1.5">
          <div
            className="h-2 rounded-full"
            role="progressbar"
            aria-valuenow={percentile}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{ backgroundColor: 'var(--background-muted)' }}
          >
            <div
              className="benchmark-fill h-full rounded-full"
              style={{
                backgroundColor: 'var(--secondary)',
                width: `${percentile}%`,
              }}
            />
          </div>
          {/* Marker dot */}
          <div
            style={{
              position: 'absolute',
              top: '-1px',
              left: `${percentile}%`,
              transform: 'translateX(-50%)',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary)',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </div>

        <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
          {t('value.benchmarkInfo')}
        </p>
      </div>

      {/* Expandable category breakdown */}
      <div
        ref={expandRef}
        style={{
          maxHeight: isExpanded ? `${expandHeight}px` : '0',
          overflow: 'hidden',
        }}
        className="benchmark-expand"
      >
        <div
          className="px-4 pb-3 pt-2 space-y-2"
          style={{ borderTop: '1px solid var(--border-soft)' }}
        >
          {benchmark.categoryPercentiles.map((cat) => {
            const catPercentile = Math.min(100, Math.max(0, cat.percentile));
            return (
              <div key={cat.categoryId} className="flex items-center gap-2">
                <span className="text-xs w-20 truncate" style={{ color: 'var(--foreground-muted)' }}>
                  {cat.categoryName}
                </span>
                <div
                  className="flex-1 h-1.5 rounded-full"
                  role="progressbar"
                  aria-valuenow={catPercentile}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('value.benchmarkCategory', { category: cat.categoryName, percentile: catPercentile })}
                  style={{ backgroundColor: 'var(--background-muted)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: 'var(--secondary)',
                      width: `${catPercentile}%`,
                    }}
                  />
                </div>
                <span className="text-xs w-8 text-right" style={{ color: 'var(--foreground-muted)' }}>
                  {cat.percentile}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .benchmark-fill { transition: width 500ms ease; }
          .benchmark-expand { transition: max-height 200ms ease; }
        }
      `}</style>
    </div>
  );
}
